import { useState, useEffect } from "react";

/**
 * 🕒 Custom hook quản lý đồng hồ đếm ngược cho từng bài test (theo testId, scenarioId, taskType)
 * - Ưu tiên hiển thị ngay thời gian từ DB (remainingTimeFromDB)
 * - Lưu tiến trình vào localStorage (key: sba_timer_${testId}_${scenarioId}_${taskType})
 * - Phục hồi tự động khi reload trang
 * - Khi hết giờ: tự động gọi onTimeUp() để nộp bài
 *
 * @param {number} initialMinutes - thời gian gốc (phút)
 * @param {string|null} testId - mã bài test để tạo storageKey riêng
 * @param {number|null} remainingTimeFromDB - thời gian còn lại (giây) từ BE
 * @param {boolean} viewMode - chế độ chỉ xem (không đếm)
 * @param {function|null} onTimeUp - callback khi hết giờ (auto submit)
 * @param {string|null} scenarioId - mã scenario hiện tại
 * @param {"reading"|"writing"|null} taskType - loại bài (reading / writing)
 */
const useTimer = (
  initialMinutes = 140,
  testId = null,
  remainingTimeFromDB = null,
  viewMode = false,
  onTimeUp = null,
  scenarioId = null,
  taskType = null
) => {
  // ✅ Key riêng cho từng bài (ví dụ: sba_timer_test12_scenario3_reading)
  const storageKey =
    testId && scenarioId && taskType
      ? `sba_timer_${testId}_${scenarioId}_${taskType}`
      : testId
        ? `sba_timer_${testId}`
        : "sba_timer_global";

  // ✅ Ưu tiên tuyệt đối: DB > localStorage > chờ DB (null) > mặc định
  const [timeRemaining, setTimeRemaining] = useState(() => {
    if (remainingTimeFromDB !== null && !isNaN(remainingTimeFromDB)) {
      console.log(`🕒 [Timer Init] Load from DB: ${remainingTimeFromDB}s`);
      localStorage.setItem(storageKey, remainingTimeFromDB);
      return Number(remainingTimeFromDB);
    }

    const saved = localStorage.getItem(storageKey);
    if (saved !== null && !isNaN(saved)) {
      console.log(`⏱️ [Timer Restore] Load from localStorage: ${saved}s`);
      return Number(saved);
    }

    console.log(`⌛ [Timer Init] Waiting for DB or default value...`);
    return null; // ⚠️ Không khởi tạo 00:00:00, chờ DB
  });

  const [isRunning, setIsRunning] = useState(true);

  // 🔄 Đồng bộ lại timer nếu BE trả về remainingTimeFromDB mới
  useEffect(() => {
    if (remainingTimeFromDB !== null && !isNaN(remainingTimeFromDB)) {
      console.log(`⚡ [Timer Sync] Update from DB: ${remainingTimeFromDB}s`);
      setTimeRemaining(Number(remainingTimeFromDB));
      localStorage.setItem(storageKey, remainingTimeFromDB);
    }
  }, [remainingTimeFromDB]);

  // 📦 Lưu mỗi khi thay đổi
  useEffect(() => {
    if (timeRemaining !== null) {
      localStorage.setItem(storageKey, timeRemaining);
    }
  }, [timeRemaining, storageKey]);

  // ⏱️ Đếm ngược mỗi giây + auto-submit khi hết giờ
  useEffect(() => {
    if (viewMode || !isRunning || timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) {
          clearInterval(timer);
          console.log(`🔴 [Timer] Hết giờ (${storageKey}) — auto-submit!`);
          localStorage.removeItem(storageKey);

          // ✅ Gọi callback onTimeUp để nộp bài tự động
          if (onTimeUp && typeof onTimeUp === "function") {
            try {
              onTimeUp();
            } catch (err) {
              console.error("❌ [Timer] onTimeUp callback error:", err);
            }
          }

          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, timeRemaining, viewMode, onTimeUp]);

  // 🧹 Khi hết giờ → xóa khỏi localStorage
  useEffect(() => {
    if (timeRemaining === 0) {
      localStorage.removeItem(storageKey);
    }
  }, [timeRemaining, storageKey]);

  // 🛑 View-only → dừng timer, không auto submit
  useEffect(() => {
    if (viewMode) {
      console.log(`👀 [Timer] View mode — stop timer (${storageKey})`);
      localStorage.removeItem(storageKey);
      setIsRunning(false);
      setTimeRemaining(initialMinutes * 60);
    }
  }, [viewMode, storageKey, initialMinutes]);

  // 🔁 Reset thủ công (khi bắt đầu lại task)
  const resetTimer = () => {
    console.log(`🔁 [Timer] Reset timer for ${storageKey}`);
    localStorage.removeItem(storageKey);
    setTimeRemaining(initialMinutes * 60);
    setIsRunning(true);
  };

  // 🧭 Định dạng hh:mm:ss
  const formatTime = (seconds) => {
    if (seconds === null || isNaN(seconds)) return "--:--:--";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return {
    timeRemaining,
    formatTime,
    setTimeRemaining,
    setIsRunning,
    resetTimer,
  };
};

export default useTimer;
